/* eslint-disable @stylistic/max-len, react/jsx-props-no-spreading */
import styled from '@emotion/styled';
import {Color, useThemeColors} from '@nfq/colors';
import {Col, Container, Row, Spacer} from '@nfq/react-grid';

import {InlineTextLink} from 'UI/components/action/InlineTextLink';
import {Section} from 'UI/components/layout/Section';
import {H2, H6, P} from 'UI/components/layout/Text';

import {SonosLayout} from 'UI/layouts/SonosLayout';

import type {NextPageWithLayout} from 'types/global';

/**
 * The `Impressum` component is a Next.js page component.
 * It is used as a route and can be accessed throught the nextjs router.
 *
 * @returns A ReactNode representing the `Impressum` page.
 */
const Impressum: NextPageWithLayout = () => {
    const colors = useThemeColors();

    return (
        <Section>
            <Spacer
                y={{
                    xs: 20, md: 10
                }}
                isNotStretching
            />
            <Container>
                <StyledRow justify="center">
                    <Col xl={10} xs={12}>
                        <H2 as="h1">Impressum</H2>
                    </Col>
                    <Col lg={6} xl={5} xs={12}>
                        <P $size="medium">
                            <Color $color={colors.secondary} as="strong">
                                Verantwortlicher Diensteanbieter i.S.v. § 5 DDG<br />
                                (Digitale-Dienste-Gesetz):<br />
                                <br />
                                SONOS EUROPE B.V.
                            </Color><br />
                            <Color $color={colors.secondary}>
                                Groest 106<br />
                                1211 EE Hilversum<br />
                                Niederlande<br />
                                <br />
                                Telefon: +31 35 62 60 520<br />
                                Kontaktformular: <InlineTextLink href="https://www.sonos.com/de-de/contact" rel="noreferrer" target="_blank">https://www.sonos.com/de-de/contact</InlineTextLink>
                            </Color>
                        </P>
                        <Spacer y={6} isNotStretching />
                        <P $size="medium">
                            <Color $color={colors.secondary}>
                                Vertretungsberechtigte: <br />
                                Volker Weisshaar<br />
                                Dick Mooijman<br />
                            </Color>
                        </P>
                        <Spacer y={6} isNotStretching />
                        <P $size="medium">
                            <Color $color={colors.secondary}>
                                Sonos Europe B.V.<br />
                                Handelskammer: 32107881<br />
                                Umsatzsteuer-ID.Nr: NL814254330B01
                            </Color>
                        </P>
                    </Col>
                    <Col lg={6} xl={5} xs={12}>
                        <P $size="medium">
                            <Color $color={colors.secondary} as="strong">
                                Technischer Betreiber der Website<br />(Auftragsverarbeiter nach Art. 28 DS-GVO):
                                <br /><br />
                                .NFQ | Netzfrequenz GmbH<br />
                            </Color>
                            <Color $color={colors.secondary}>
                                Stadtberger Straße 99<br />
                                86157 Augsburg
                            </Color>
                        </P>
                        <Spacer y={6} isNotStretching />
                        <P $size="medium">
                            <Color $color={colors.secondary}>
                                Telefon: +49 821 806 812 00<br />
                                E-Mail: <InlineTextLink href="mailto:info@nfq.de">info@nfq.de</InlineTextLink><br />
                                Web: <InlineTextLink href="https://www.nfq.de" rel="noreferrer" target="_blank">www.nfq.de</InlineTextLink>
                            </Color>
                        </P>
                        <Spacer y={6} isNotStretching />
                        <P $size="medium">
                            <Color $color={colors.secondary}>
                                Vertretungsberechtigter: Andreas Stegmann<br />
                                Amtsgericht Augsburg<br />
                                Handelsregisternummer: HRB 17164
                            </Color>
                        </P>
                    </Col>
                    <Col xl={10} xs={12}>
                        <H6 as="h2">Haftung</H6>
                        <Spacer y={3} isNotStretching />
                        <P $size="medium">
                            <Color $color={colors.secondary}>
                                Wir sind für die Inhalte unserer Internetseiten nach den Maßgaben der allgemeinen Gesetze verantwortlich. Alle Inhalte werden mit der gebotenen Sorgfalt und nach bestem Wissen erstellt. Soweit wir auf unseren Internetseiten mittels Hyperlink auf Internetseiten Dritter verweisen, können wir keine Gewähr für die fortwährende Aktualität, Richtigkeit und Vollständigkeit der verlinkten Inhalte übernehmen, da diese Inhalte außerhalb unseres Verantwortungsbereichs liegen und wir auf die zukünftige Gestaltung keinen Einfluss haben. Sollten aus Ihrer Sicht Inhalte gegen geltendes Recht verstoßen oder unangemessen sein, teilen Sie uns dies bitte mit.<br />
                                <br />
                                Die rechtlichen Hinweise auf dieser Seite sowie alle Fragen und Streitigkeiten im Zusammenhang mit der Gestaltung dieser Internetseite unterliegen dem Recht der Bundesrepublik Deutschland.
                            </Color>
                        </P>
                    </Col>
                    <Col xl={10} xs={12}>
                        <H6 as="h2">
                            Datenschutzhinweise
                        </H6>
                        <Spacer y={3} isNotStretching />
                        <P $size="medium">
                            <Color $color={colors.secondary}>
                                Bitte lesen Sie hierzu unsere Datenschutzhinweise.
                            </Color>
                        </P>
                    </Col>
                    <Col xl={10} xs={12}>
                        <H6 as="h2">Urheberrechtshinweis</H6>
                        <Spacer y={3} isNotStretching />
                        <P $size="medium">
                            <Color $color={colors.secondary}>
                                Alle Inhalte dieser Webseite (Bilder, Fotos, Texte, Videos) unterliegen dem Urheberrecht. Jede unberechtigte Verwendung (insbesondere die Vervielfältigung, Bearbeitung oder Verbreitung) dieser urheberrechtsgeschützten Inhalte ist daher untersagt. Wenn Sie beabsichtigen, diese Inhalte oder Teile davon zu verwenden, kontaktieren Sie uns bitte im Voraus unter den obenstehenden Angaben. Soweit wir nicht selbst Inhaber der benötigten urheberrechtlichen Nutzungsrechte sein sollten, bemühen wir uns, einen Kontakt zum Berechtigten zu vermitteln.<br />
                                <br />
                                Rechteinhaber:<br />
                                SONOS, INC.<br />
                                614 Chapala St.,<br />
                                Santa Barbara, CA,<br />
                                93101, USA
                            </Color>
                        </P>
                    </Col>
                </StyledRow>
            </Container>
        </Section>
    );
};

/**
 * `getLayout` is a static property of the `Impressum` component that is a function designed to wrap the component with a specified layout.
 * It receives the router object, page properties, and the PageComponent and returns the PageComponent wrapped with the necessary layout and properties.
 * This property is essential for applying consistent layouts across different pages in the application.
 *
 * @param router        The Next.js router object representing the current route of the application.
 * @param pageProps     The page properties object representing the properties of the current page.
 * @param PageComponent The PageComponent representing the current page.
 * @returns A ReactNode representing the PageComponent wrapped with the necessary layout and properties.
 *
 * @example
 * ```tsx
 * const LayoutWrappedComponent = Impressum.getLayout(router, pageProps, PageComponent);
 * ```
 */
Impressum.getLayout = (router, pageProps, PageComponent) => (
    <SonosLayout testId="datenschutzPageLayout" title="Sonos Ace | Impressum">
        <PageComponent router={router} {...pageProps} />
    </SonosLayout>
);

/**
 * `getLayoutKey` is a static property of the `Impressum` component that is a function designed to return the layout key string.
 * It is used to determine if the layout changed between pages to apply transitions between different layouts.
 * This property is crucial for managing different layouts within the application and for providing a specific layout key for the `Impressum` component.
 *
 * @returns A string representing the layout key.
 *
 * @example
 * ```tsx
 * const layoutKey = Impressum.getLayoutKey();
 * ```
 */
Impressum.getLayoutKey = () => '';

export default Impressum;

const StyledRow = styled(Row)`
    row-gap: 6rem;
`;